/**
 * Locale Detection Utility
 *
 * Safely retrieves the current locale from window.locale or URL path.
 * Reports errors via ErrorReporter if locale cannot be determined.
 *
 * Usage:
 *   import { getLocale, getLocaleOrDisconnect } from '../utils/locale'
 *
 *   // Simple usage - returns locale or null
 *   const locale = getLocale()
 *   if (!locale) return
 *
 *   // In Stimulus controllers - automatically disconnects on failure
 *   const locale = getLocaleOrDisconnect(this)
 *   if (!locale) return
 *
 * Supported locales: en-US, en-CA, fr-CA
 */

const VALID_LOCALES = ['en-US', 'en-CA', 'fr-CA']
const LOCALE_REGEX = /^\/(en-US|en-CA|fr-CA)\//

/**
 * Extract locale from the current URL path
 * @returns {string|null} The locale if found in URL, null otherwise
 */
function extractLocaleFromUrl() {
  if (typeof window === 'undefined') return null

  const path = window.location.pathname
  const match = path.match(LOCALE_REGEX)

  if (match && match[1]) {
    return match[1]
  }

  return null
}

/**
 * Get the current locale from window.locale or URL path
 * Reports error via ErrorReporter if locale cannot be determined
 *
 * @param {Object} options - Optional configuration
 * @param {boolean} options.silent - If true, don't report error (useful for testing)
 * @returns {string|null} The locale (e.g., 'en-US') or null if not available
 */
export function getLocale({ silent = false } = {}) {
  // Try window.locale first (set by Rails)
  if (typeof window !== 'undefined' && window.locale && VALID_LOCALES.includes(window.locale)) {
    return window.locale
  }

  // Fall back to URL extraction
  const urlLocale = extractLocaleFromUrl()
  if (urlLocale) {
    return urlLocale
  }

  // Report error - this shouldn't happen in normal operation
  if (!silent) {
    const error = new Error('Locale could not be determined from window.locale or URL path')
    const context = {
      windowLocale: typeof window !== 'undefined' ? window.locale : 'undefined',
      pathname: typeof window !== 'undefined' ? window.location.pathname : 'undefined',
      href: typeof window !== 'undefined' ? window.location.href : 'undefined'
    }

    // Report to AppSignal if available
    if (typeof window !== 'undefined' && window.ErrorReporter && typeof window.ErrorReporter.error === 'function') {
      window.ErrorReporter.error(error, context)
    } else {
      console.error('[locale]', error.message, context)
    }
  }

  return null
}

/**
 * Get locale or disconnect the Stimulus controller if locale cannot be determined
 * Use this in Stimulus controller connect() methods
 *
 * @param {Object} controller - The Stimulus controller instance (must have disconnect method)
 * @param {Object} options - Optional configuration
 * @param {string} options.controllerName - Name to include in error message
 * @returns {string|null} The locale or null (controller will be disconnected)
 *
 * @example
 * connect() {
 *   this.locale = getLocaleOrDisconnect(this)
 *   if (!this.locale) return
 *   // ... rest of connect logic
 * }
 */
export function getLocaleOrDisconnect(controller, { controllerName = 'unknown' } = {}) {
  const locale = getLocale({ silent: true })

  if (!locale) {
    const error = new Error(`Stimulus controller '${controllerName}' cannot operate without locale`)
    const context = {
      controller: controllerName,
      element: controller.element?.tagName,
      windowLocale: typeof window !== 'undefined' ? window.locale : 'undefined',
      pathname: typeof window !== 'undefined' ? window.location.pathname : 'undefined'
    }

    // Report to AppSignal
    if (typeof window !== 'undefined' && window.ErrorReporter && typeof window.ErrorReporter.error === 'function') {
      window.ErrorReporter.error(error, context)
    } else {
      console.error('[locale]', error.message, context)
    }

    // Disconnect the controller
    if (controller && typeof controller.disconnect === 'function') {
      // Use queueMicrotask to avoid calling disconnect during connect
      queueMicrotask(() => controller.disconnect())
    }

    return null
  }

  return locale
}

/**
 * Get the currency for a given locale
 * @param {string} locale - The locale (e.g., 'en-US', 'en-CA')
 * @returns {string} The currency code (USD or CAD)
 */
export function getCurrencyForLocale(locale) {
  return locale === 'en-CA' || locale === 'fr-CA' ? 'CAD' : 'USD'
}

/**
 * Build a localized URL path
 * @param {string} path - The path without locale prefix (e.g., '/my_cart/add_item.json')
 * @param {string} locale - The locale to use (optional, uses getLocale() if not provided)
 * @returns {string|null} The full path with locale prefix, or null if locale unavailable
 *
 * @example
 * buildLocalePath('/my_cart/add_item.json')
 * // Returns: '/en-US/my_cart/add_item.json'
 */
export function buildLocalePath(path, locale = null) {
  const loc = locale || getLocale()
  if (!loc) return null

  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  return `/${loc}${normalizedPath}`
}

export default getLocale

